/**
 * Platform Tokens
 *
 * Shared semantic tone, density, and motion class maps for the Losi
 * Operating Surface.
 *
 * Primary responsibilities:
 * - Keep status colors named by meaning instead of hue
 * - Keep surface density and motion rules reusable across UI primitives
 *
 * Dependencies:
 * - Tailwind semantic tokens
 */

export type PlatformTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'agent'

export type PlatformDensity = 'compact' | 'default' | 'spacious'

export interface PlatformToneClasses {
  badge: string
  badgeDot: string
  icon: string
  surface: string
  ring: string
  text: string
}

export const platformToneClasses: Record<PlatformTone, PlatformToneClasses> = {
  neutral: {
    badge: 'border-border bg-muted text-muted-foreground',
    badgeDot: 'bg-muted-foreground',
    icon: 'bg-muted text-muted-foreground',
    surface: 'border-border bg-card text-card-foreground',
    ring: 'focus-visible:ring-ring',
    text: 'text-muted-foreground'
  },
  success: {
    badge:
      'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/50 dark:text-emerald-300',
    badgeDot: 'bg-emerald-500',
    icon: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
    surface:
      'border-emerald-200/80 bg-emerald-50/60 text-emerald-950 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-100',
    ring: 'focus-visible:ring-emerald-500/40',
    text: 'text-emerald-700 dark:text-emerald-300'
  },
  warning: {
    badge:
      'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/50 dark:text-amber-300',
    badgeDot: 'bg-amber-500',
    icon: 'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
    surface:
      'border-amber-200/80 bg-amber-50/60 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100',
    ring: 'focus-visible:ring-amber-500/40',
    text: 'text-amber-800 dark:text-amber-300'
  },
  danger: {
    badge:
      'border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/50 dark:text-red-300',
    badgeDot: 'bg-red-500',
    icon: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300',
    surface:
      'border-red-200/80 bg-red-50/60 text-red-950 dark:border-red-900/60 dark:bg-red-950/20 dark:text-red-100',
    ring: 'focus-visible:ring-red-500/40',
    text: 'text-red-700 dark:text-red-300'
  },
  info: {
    badge:
      'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/70 dark:bg-sky-950/50 dark:text-sky-300',
    badgeDot: 'bg-sky-500',
    icon: 'bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300',
    surface:
      'border-sky-200/80 bg-sky-50/60 text-sky-950 dark:border-sky-900/60 dark:bg-sky-950/20 dark:text-sky-100',
    ring: 'focus-visible:ring-sky-500/40',
    text: 'text-sky-700 dark:text-sky-300'
  },
  agent: {
    badge:
      'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/70 dark:bg-violet-950/50 dark:text-violet-300',
    badgeDot: 'bg-violet-500',
    icon: 'bg-violet-50 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300',
    surface:
      'border-violet-200/80 bg-violet-50/60 text-violet-950 dark:border-violet-900/60 dark:bg-violet-950/20 dark:text-violet-100',
    ring: 'focus-visible:ring-violet-500/40',
    text: 'text-violet-700 dark:text-violet-300'
  }
}

export const platformDensityClasses: Record<
  PlatformDensity,
  { surface: string; emptyState: string; metric: string; icon: string }
> = {
  compact: {
    surface: 'gap-3 p-3',
    emptyState: 'gap-2 px-4 py-5',
    metric: 'gap-1 px-3 py-2',
    icon: 'size-8'
  },
  default: {
    surface: 'gap-4 p-4 sm:p-5',
    emptyState: 'gap-3 px-5 py-8',
    metric: 'gap-1.5 px-4 py-3',
    icon: 'size-10'
  },
  spacious: {
    surface: 'gap-5 p-5 sm:p-6',
    emptyState: 'gap-4 px-6 py-12',
    metric: 'gap-2 px-5 py-4',
    icon: 'size-12'
  }
}

export const platformMotionClasses = {
  control:
    'motion-safe:transition-[background-color,border-color,color,box-shadow,opacity,transform] motion-safe:duration-200 motion-safe:ease-out motion-reduce:transition-none motion-reduce:transform-none',
  panel:
    'motion-safe:transition-[background-color,border-color,box-shadow,opacity,transform] motion-safe:duration-200 motion-safe:ease-out motion-reduce:transition-none motion-reduce:transform-none',
  overlay: 'motion-safe:transition-opacity motion-safe:duration-200 motion-reduce:transition-none',
  loading: 'motion-safe:animate-spin motion-reduce:animate-none',
  pulse: 'motion-safe:animate-pulse motion-reduce:animate-none'
} as const
